import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ShieldCheck, Zap, Globe, Users } from 'lucide-react';

import papaimage from "../assets/papa.png";

function Home() {
  const navigate = useNavigate();

  return (
    <div className="landing-wrapper">
      <Navbar />
      
      {/* --- HERO SECTION --- */}
      <section className="hero-modern-wrap" style={{ background: '#F8F8F7', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
        <div className="hero-modern container section-padding">
          <div className="hero-content">
            <span className="eyebrow">Vastu & Architectural Planning</span>
            <h1 className="hero-title">
              Architecture of <i>Prosperity</i>.
            </h1>
            <p className="hero-description">
              VastuZone provides clear, practical Vastu consultation for homes, offices, and commercial spaces. 
              We combine traditional Vastu principles with modern architectural planning to help create balanced, functional environments.
            </p>
            <div className="hero-actions">
              <button className="btn-primary" onClick={() => navigate("/login")}>
                Start Analysis
              </button>
              <button className="btn-secondary" onClick={() => document.getElementById('report-details').scrollIntoView({behavior: 'smooth'})}>
                The Report Methodology
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <img
              src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80&w=1200"
              alt="Minimalist Luxury Interior"
              className="hero-main-img"
            />
          </div>
        </div>
      </section>

      {/* --- TRUST BAR --- */}
      <section className="trust-bar-lux">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <ShieldCheck size={20} color="var(--brass)" />
              <span>Certified Vastu Expert</span>
            </div>
            <div className="trust-item">
              <Zap size={20} color="var(--brass)" />
              <span>Practical Floor Plan Audits</span>
            </div>
            <div className="trust-item">
              <Globe size={20} color="var(--brass)" />
              <span>Residential & Commercial</span>
            </div>
            <div className="trust-item">
              <Users size={20} color="var(--brass)" />
              <span>Non-Structural Remedies</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- VASTU INTRODUCTION & FOUNDATION SECTION --- */}
      <section id="about-vastu" className="vastu-intro-section section-padding">
        <div className="container">
          {/* Two-column layout on desktop: Left Quote, Right Father's Photo */}
          <div className="vastu-intro-grid">
            <div className="vastu-quote-col">
              <span className="eyebrow">विचारधारा • Perspective</span>
              <div className="vastu-quote-box">
                <blockquote className="vastu-quote-text">
                  “वास्तुशास्त्र न तो कोई चमत्कार है और न ही जादू-टोना। यह एक प्राचीन ज्ञान-विज्ञान है, जिसका उपयोग भवनों और कार्यस्थलों की योजना और निर्माण में किया जाता रहा है। आज भी अनेक लोग अपने घरों, कार्यालयों, व्यावसायिक भवनों और उद्योगों के निर्माण में वास्तु के सिद्धांतों को ध्यान में रखते हैं।”
                </blockquote>
                <div className="vastu-quote-attribution">
                  <p className="vastu-expert-name">डॉ. रजनी कान्त श्रीवास्तव</p>
                  <p className="vastu-expert-role">वरिष्ठ वास्तु विशेषज्ञ / Vastu Consultant</p>
                </div>
              </div>
            </div>

            <div className="vastu-photo-col">
              <div className="vastu-photo-card">
                <img
                  src={papaimage}
                  alt="Dr. Rajni Kant Srivastava - Vastu Expert"
                  className="vastu-expert-portrait"
                />
                <div className="vastu-photo-badge">
                  <p className="badge-name">Dr. Rajni Kant Srivastava</p>
                  <span className="badge-title">Vastu Shastra Consultant</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full-width section below: Basic Foundation of Vastu */}
          <div className="vastu-foundation-wrapper">
            <div className="foundation-header">
              <h2 className="foundation-heading">वास्तु का आधार</h2>
            </div>
            <div className="foundation-body">
              <p>
                वास्तु को सरल शब्दों में समझें तो यह भवन, उसके आसपास के वातावरण और उसमें मौजूद विभिन्न तत्वों के बीच संतुलन से जुड़ा हुआ है।
              </p>
              <p>
                वास्तु में दिशाओं, पंचतत्वों, प्रकाश, वायु, रंगों, पौधों और भवन में रखी वस्तुओं के उचित उपयोग पर ध्यान दिया जाता है। माना जाता है कि किसी स्थान की संरचना और उसमें मौजूद वस्तुओं का वातावरण पर प्रभाव पड़ता है।
              </p>
              <p>
                एक वास्तु विशेषज्ञ का कार्य इन सिद्धांतों को समझकर उनका व्यावहारिक और सकारात्मक उपयोग करना है, ताकि भवन का वातावरण अधिक संतुलित और उपयोगी बनाया जा सके।
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- SERVICES GRID --- */}
      <section className="services-section section-padding">
        <div className="container">
          <div className="section-header center-text">
            <span className="eyebrow">Consultation Services</span>
            <h2 className="serif">Vastu Consultation Services</h2>
          </div>
          
          <div className="services-grid-modern">
            <div className="service-card-premium">
              <span className="card-cat">Residential</span>
              <h3>Vastu for Homes & Residences</h3>
              <p>Comprehensive layout and direction analysis for independent houses, villas, and residential properties. Practical recommendations for room placement and harmonious living without structural demolition.</p>
            </div>
            <div className="service-card-premium">
              <span className="card-cat">Commercial</span>
              <h3>Vastu for Offices & Workspaces</h3>
              <p>Orientation and seating guidance for offices, corporate setups, and commercial spaces to support productivity, smooth workflow, and a positive working environment.</p>
            </div>
            <div className="service-card-premium">
              <span className="card-cat">Industrial</span>
              <h3>Vastu for Factories & Industries</h3>
              <p>Practical planning for industrial units, including machinery placement, raw material storage, and administrative areas to support efficient and safe operations.</p>
            </div>
            <div className="service-card-premium">
              <span className="card-cat">Geometric</span>
              <h3>Vastu for Plot & Land Selection</h3>
              <p>Guidance on plot shape, slope, road alignment, and surrounding environment before purchasing land or beginning new construction.</p>
            </div>
            <div className="service-card-premium">
              <span className="card-cat">Urban</span>
              <h3>Vastu for Flats & Apartments</h3>
              <p>Practical assessment of apartment floor plans, main entrances, and room orientations with simple, non-structural adjustments suited for apartment living.</p>
            </div>
            <div className="service-card-premium">
              <span className="card-cat">Design</span>
              <h3>Vastu for Architectural Planning</h3>
              <p>Collaborative guidance with your architect or engineer to design floor plans aligned with core Vastu principles from the initial drafting stage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- REPORT CONTENTS --- */}
      <section id="report-details" className="report-contents-section section-padding" style={{ background: '#0A0A0A', color: 'var(--paper)' }}>
        <div className="container">
          <div className="report-split">
            <div className="report-info">
              <span className="eyebrow" style={{ color: 'var(--brass)' }}>Report Overview</span>
              <h2 className="serif" style={{ fontSize: '3.5rem' }}>Detailed Vastu Audit Report</h2>
              <p style={{ opacity: 0.8, marginBottom: '40px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                A comprehensive and personalized analysis based on your property layout, direction, and usage — 
                designed to provide clear, practical, and effective Vastu recommendations.
              </p>
              
              <ul className="report-list-premium">
                <li>Direction & Layout Analysis (using floor plans and map reference)</li>
                <li>Zone-by-zone layout and orientation evaluation</li>
                <li>Identification of imbalances and practical recommendations</li>
                <li>Practical adjustments without major structural changes</li>
                <li>Room-wise guidance (entrance, kitchen, bedroom, etc.)</li>
                <li>Interior placement and seating recommendations</li>
                <li>Colour and lighting balancing suggestions</li>
                <li>Simple remedies for everyday living</li>
              </ul>
            </div>
            <div className="report-visual-card">
              <div className="glass-overlay">
                <span className="serif report-card-title">CUSTOMIZED REPORTS</span>
                <p className="eyebrow" style={{ color: '#FFF', marginTop: '20px', textAlign: 'center', lineHeight: '1.4' }}>
                  Comprehensive Analysis <br /> Tailored to Your Space
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS --- */}
      <section className="testimonials section-padding" style={{ background: 'var(--paper)' }}>
        <div className="container">
          <div className="section-header center-text">
            <span className="eyebrow">Client Feedback</span>
            <h2 className="serif">Client Testimonials</h2>
          </div>
          <div className="testimonial-grid-premium">
            <div className="testimonial-card-lux">
              <p>"I had a great experience consulting the VastuZone service. The analysis was very detailed and practical, and the suggestions were easy to implement without major changes. I noticed a positive shift in the overall energy and comfort of my space. Highly recommended for anyone looking for genuine and knowledgeable guidance."</p>
              <div className="testi-author">
                <span className="author-name">Aditya kumar Jha</span>
                <span className="author-loc">Dhanbad</span>
              </div>
            </div>
            <div className="testimonial-card-lux">
              <p>"The advice was simple and actually made sense. Made a few small changes at home and things already feel better. Definitely worth trying if you are curious about Vastu."</p>
              <div className="testi-author">
                <span className="author-name">Samarth Dixit</span>
                <span className="author-loc">Lucknow</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer-expanded section-padding">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-col">
              <h3 className="serif" style={{ fontSize: '2rem' }}>VastuZone</h3>
              <p style={{ marginTop: '15px' }}>Practical Vastu consultancy and architectural guidance for balanced living and working spaces.</p>
            </div>
            <div className="footer-col">
              <h4>Consultancy</h4>
              <button className="footer-link" onClick={() => { navigate("/add-property"); window.scrollTo(0,0); }}>Residential Audit</button>
              <button className="footer-link" onClick={() => { navigate("/book-appointment"); window.scrollTo(0,0); }}>Expert Strategy</button>
              <button className="footer-link" onClick={() => { navigate("/chat"); window.scrollTo(0,0); }}>Live Consultation</button>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <button className="footer-link" onClick={() => { navigate("/terms"); window.scrollTo(0,0); }}>Terms of Service</button>
              <button className="footer-link" onClick={() => { navigate("/privacy"); window.scrollTo(0,0); }}>Privacy Policy</button>
              <button className="footer-link" onClick={() => { navigate("/refund-policy"); window.scrollTo(0,0); }}>Refund Policy</button>
            </div>
            <div className="footer-col">
              <h4>Contact</h4>
              <p>kant.online@gmail.com</p>
              <p>Lucknow, India</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 VastuZone. All rights reserved. Coded by Rajat </p>
          </div>
        </div>
      </footer>

      <style>{`
        .landing-wrapper { overflow-x: hidden; }
        .footer-link { 
          display: block; 
          background: none; 
          border: none; 
          padding: 0; 
          color: var(--text-muted); 
          font-size: 0.9rem; 
          margin-bottom: 15px; 
          cursor: pointer; 
          text-align: left;
          transition: 0.3s;
        }
        .footer-link:hover { color: var(--ink); padding-left: 5px; }
        .eyebrow { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.3em; color: var(--brass); font-weight: 700; margin-bottom: 20px; display: block; }
        
        .hero-modern { display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 80px; align-items: center; }
        .hero-title { font-size: 5.5rem; line-height: 0.95; margin-bottom: 30px; color: #111; }
        .hero-title i { font-family: 'Instrument Serif', serif; font-style: italic; color: var(--brass); }
        .hero-description { font-size: 1.2rem; color: var(--text-muted); max-width: 540px; margin-bottom: 40px; line-height: 1.7; }
        .hero-actions { display: flex; gap: 20px; }
        .hero-main-img { border-radius: 0; box-shadow: 40px 40px 0 var(--stone); width: 100%; height: auto; scale: 1.15; transition: var(--transition-smooth); }
        .hero-main-img:hover { scale: 1.18; }

        /* Trust Bar */
        .trust-bar-lux { background: #FFF; border-bottom: 1px solid var(--stone); padding: 40px 0; }
        .trust-grid { display: flex; justify-content: space-between; align-items: center; gap: 30px; }
        .trust-item { display: flex; align-items: center; gap: 12px; }
        .trust-item span { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-muted); }
        @media (max-width: 768px) {
          .trust-grid { flex-direction: column; align-items: flex-start; gap: 20px; }
        }

        .section-header { margin-bottom: 80px; }
        .section-header h2 { font-size: 4rem; color: #111; }

        /* --- VASTU INTRO & PHILOSOPHY SECTION --- */
        .vastu-intro-section { background: #FCFCFA; border-bottom: 1px solid rgba(26, 28, 25, 0.06); }
        .vastu-intro-grid { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 70px; align-items: center; margin-bottom: 70px; }
        .vastu-quote-col { display: flex; flex-direction: column; }
        .vastu-quote-box { position: relative; border-left: 3px solid var(--brass); padding-left: 30px; }
        .vastu-quote-text { 
          font-family: 'Noto Serif Devanagari', 'Instrument Serif', Georgia, serif; 
          font-size: 1.45rem; 
          line-height: 2; 
          color: var(--ink); 
          font-weight: 500; 
          margin-bottom: 24px; 
          letter-spacing: 0.01em;
        }
        .vastu-quote-attribution { display: flex; flex-direction: column; gap: 4px; }
        .vastu-expert-name { 
          font-family: 'Plus Jakarta Sans', 'Noto Sans Devanagari', sans-serif; 
          font-size: 1.05rem; 
          font-weight: 700; 
          color: var(--ink); 
          letter-spacing: 0.02em; 
        }
        .vastu-expert-role { 
          font-size: 0.85rem; 
          color: var(--brass); 
          font-weight: 600; 
          text-transform: uppercase; 
          letter-spacing: 0.12em; 
        }

        .vastu-photo-col { display: flex; justify-content: center; align-items: center; }
        .vastu-photo-card { 
          width: 100%; 
          max-width: 440px; 
          background: #FFF; 
          border: 1px solid rgba(0, 0, 0, 0.08); 
          box-shadow: 0 12px 36px rgba(0, 0, 0, 0.05); 
          border-radius: 4px; 
          overflow: hidden; 
        }
        .vastu-expert-portrait { 
          width: 100%; 
          height: 420px; 
          object-fit: cover; 
          object-position: center top; 
          display: block; 
        }
        .vastu-photo-badge { 
          padding: 16px 20px; 
          background: var(--paper); 
          border-top: 1px solid rgba(0, 0, 0, 0.05); 
          text-align: center; 
        }
        .badge-name { 
          font-family: 'Instrument Serif', serif; 
          font-size: 1.35rem; 
          color: var(--ink); 
          margin-bottom: 2px; 
          line-height: 1.2; 
        }
        .badge-title { 
          font-size: 0.75rem; 
          text-transform: uppercase; 
          letter-spacing: 0.2em; 
          color: var(--brass); 
          font-weight: 700; 
          display: block; 
        }

        /* Vastu Foundation (Full Width Below) */
        .vastu-foundation-wrapper { border-top: 1px solid rgba(0, 0, 0, 0.08); padding-top: 60px; }
        .foundation-header { margin-bottom: 28px; }
        .foundation-heading { 
          font-family: 'Noto Serif Devanagari', 'Instrument Serif', Georgia, serif; 
          font-size: 2.5rem; 
          color: var(--ink); 
          font-weight: 600; 
          line-height: 1.2; 
          display: inline-block; 
        }
        .foundation-heading::after { 
          content: ''; 
          display: block; 
          width: 44px; 
          height: 2px; 
          background: var(--brass); 
          margin-top: 12px; 
        }
        .foundation-body { 
          max-width: 980px; 
          display: flex; 
          flex-direction: column; 
          gap: 20px; 
        }
        .foundation-body p { 
          font-family: 'Noto Sans Devanagari', 'Plus Jakarta Sans', sans-serif; 
          font-size: 1.15rem; 
          line-height: 2; 
          color: var(--text-main); 
          margin: 0; 
        }

        /* Services Grid - Modern Luxury */

        .services-grid-modern { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; }
        .service-card-premium { background: #111; padding: 60px 40px; border-radius: 2px; transition: var(--transition-smooth); border: 1px solid rgba(176, 141, 87, 0.1); }
        .service-card-premium:hover { transform: translateY(-10px); border-color: var(--brass); box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
        .card-cat { font-size: 0.65rem; text-transform: uppercase; color: var(--brass); letter-spacing: 0.2em; margin-bottom: 20px; display: block; font-weight: 700; }
        .service-card-premium h3 { color: #FFF; font-size: 1.6rem; margin-bottom: 20px; font-family: 'Instrument Serif', serif; }
        .service-card-premium p { color: rgba(255,255,255,0.6); font-size: 0.9rem; line-height: 1.8; }

        /* Report Section */
        .report-split { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 100px; align-items: center; }
        .report-list-premium { list-style: none; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
        .report-list-premium li { font-size: 0.95rem; opacity: 0.8; padding-bottom: 15px; border-bottom: 1px solid rgba(255,255,255,0.1); transition: 0.3s; }
        .report-list-premium li:hover { border-color: var(--brass); color: var(--brass); }

        .report-visual-card { background: #151515; height: 500px; border: 1px solid rgba(176, 141, 87, 0.3); display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; max-width: 100%; }
        .glass-overlay { text-align: center; max-width: 100%; padding: 20px; }
        .report-card-title { font-size: 3.5rem; display: block; line-height: 1.1; font-weight: 400; color: var(--brass); }

        /* Testimonials LUX - Fixed Bottom Author */
        .testimonial-grid-premium { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
        .testimonial-card-lux { padding: 60px; background: var(--stone); border-radius: 2px; transition: 0.3s; display: flex; flex-direction: column; justify-content: space-between; min-height: 320px; }
        .testimonial-card-lux:hover { background: #FFF; box-shadow: var(--shadow-soft); }
        .testimonial-card-lux p { font-size: 1.2rem; line-height: 1.8; color: var(--text-main); font-family: 'Instrument Serif', serif; font-style: italic; margin-bottom: 30px; }
        .testi-author { border-top: 1px solid rgba(0,0,0,0.05); padding-top: 20px; margin-top: auto; }
        .author-name { display: block; font-weight: 700; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--ink); }
        .author-loc { font-size: 0.8rem; color: var(--brass); }

        .footer-expanded { background: var(--paper); border-top: var(--border-subtle); color: var(--ink); }
        .footer-grid { display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr; gap: 40px; }
        .footer-col h4 { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.15em; margin-bottom: 30px; color: var(--brass); font-weight: 700; }
        .footer-col a, .footer-col p { display: block; font-size: 0.9rem; color: var(--text-muted); margin-bottom: 15px; text-decoration: none; transition: 0.3s; }
        .footer-col a:hover { color: var(--ink); padding-left: 5px; }

        @media (max-width: 1000px) {
          .hero-modern, .report-split, .services-grid-modern, .testimonial-grid-premium, .footer-grid { grid-template-columns: 1fr; gap: 50px; }
          .hero-title { font-size: 3.5rem; }
          .report-list-premium { grid-template-columns: 1fr; }
          .vastu-intro-grid { grid-template-columns: 1fr; gap: 45px; margin-bottom: 50px; }
          .vastu-photo-card { max-width: 440px; margin: 0 auto; }
        }

        @media (max-width: 768px) {
          .hero-modern { gap: 40px; }
          .hero-title { font-size: 2.8rem; }
          .hero-main-img { scale: 1; box-shadow: 15px 15px 0 var(--stone); }
          .hero-visual { overflow: hidden; max-width: 100%; }
          .section-header h2 { font-size: 2.5rem; }
          .vastu-intro-grid { grid-template-columns: 1fr; gap: 40px; margin-bottom: 40px; }
          .vastu-quote-text { font-size: 1.25rem; line-height: 1.9; }
          .vastu-expert-portrait { height: 380px; }
          .foundation-heading { font-size: 2.1rem; }
          .foundation-body p { font-size: 1.05rem; line-height: 1.9; }
          .vastu-foundation-wrapper { padding-top: 45px; }
          .report-visual-card { height: 320px; }
          .report-card-title { font-size: 2.2rem; }
        }

        @media (max-width: 480px) {
          .hero-title { font-size: 2.2rem; word-break: break-word; }
          .hero-description { font-size: 1rem; }
          .hero-actions { flex-direction: column; width: 100%; }
          .hero-actions button { width: 100%; }
          .hero-main-img { scale: 1; box-shadow: 10px 10px 0 var(--stone); }
          .section-header h2 { font-size: 2rem; }
          .service-card-premium { padding: 35px 20px; }
          .testimonial-card-lux { padding: 35px 20px; min-height: auto; }
          .vastu-quote-box { padding-left: 16px; border-left-width: 2px; }
          .vastu-quote-text { font-size: 1.12rem; line-height: 1.85; }
          .vastu-expert-portrait { height: 300px; }
          .foundation-heading { font-size: 1.75rem; }
          .foundation-body p { font-size: 0.98rem; line-height: 1.85; }
          .report-card-title { font-size: 1.7rem; }
        }
      `}</style>
    </div>
  );
}

export default Home;
